import { useCallback } from 'react'
import {
  Button,
  Heading,
  Image,
  List,
  Text,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import styles from './SavedRepliesPaywal.module.css'

type CtaOptions = {
  view: string
  location: string
  button: string
}

type SavedRepliesPaywallProps = {
  open: boolean
  setOpen: (open: boolean) => void
  ctaProperties: CtaOptions
  upgradePathName: string
}

export function SavedRepliesPaywall({
  open,
  setOpen,
  ctaProperties,
  upgradePathName,
}: SavedRepliesPaywallProps): JSX.Element {
  const currentOrganizationId = useOrganizationId()

  const { view, location, button } = ctaProperties
  const cta = `publish-${view}-${location}-${button}-1` as const

  const SAVED_REPLIES_PAYWALL_DATA = {
    heading: 'Upgrade to create more Saved Replies',
    body: 'Save information you share often to quickly insert it when replying to your comments.',
    bulletPoints: [
      'Unlimited saved replies',
      'Unlimited comment reply suggestions',
      'Schedule up to 2000 posts at a time',
      'Advanced publishing and planning features',
      'In-depth social analytics',
    ],
    ctaText: 'See Upgrade Options',
  }

  const onCTAClick = (): void => {
    setOpen(false)
    window?.appshell.actions.openModal(window?.appshell.MODALS.planSelector, {
      cta,
      upgradePathName,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - FIXME: Object literal may only specify known properties, and 'planSelector' does not exist in type 'ModalData'
      planSelector: {
        initialStep: 'upgrade',
      },
    })
  }

  const handleOpen = useCallback(
    (open: boolean) => {
      if (open) {
        BufferTracker.cTAClicked({
          clientName: Client.PublishWeb,
          product: 'publish',
          cta,
          upgradePathName,
          organizationId: currentOrganizationId ?? '',
        })
        BufferTracker.upgradePathViewed({
          clientName: Client.PublishWeb,
          product: 'publish',
          organizationId: currentOrganizationId ?? '',
          upgradePathName,
        })
        // Send event to Clarity to easily track errors in the community
        if (window.clarity) {
          window.clarity(
            'set',
            'community_comment_saved_replies_paywall_viewed',
          )
        }
      }
      setOpen(open)
    },
    [open, setOpen, cta, upgradePathName, currentOrganizationId],
  )

  return (
    <UpgradeDialog open={open} onOpenChange={(open) => handleOpen(open)}>
      <UpgradeDialog.Content size="xlarge">
        <UpgradeDialog.TextContent>
          <Heading size="large">{SAVED_REPLIES_PAYWALL_DATA.heading}</Heading>
          <Text>{SAVED_REPLIES_PAYWALL_DATA.body}</Text>
          <List className={styles.paywallList}>
            {SAVED_REPLIES_PAYWALL_DATA.bulletPoints.map((item, index) => {
              return (
                <List.Item key={`suggestions-paywall-details-${index}`}>
                  {item}
                </List.Item>
              )
            })}
          </List>
          <Button variant="primary" size="large" onClick={onCTAClick}>
            {SAVED_REPLIES_PAYWALL_DATA.ctaText}
          </Button>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            alt="Upgrade to have unlimited reply suggestions"
            placeholder="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            className={styles.paywallImage}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
